import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from "yup";
import { toast } from 'react-toastify';
import './EcwOrderAdministration.scss';
import { Button, Form } from 'react-bootstrap';
import { FormikProps, useFormik } from 'formik';
import useAdmin from '../../Hooks/useAdmin';
import useEcwOrders from '../../Hooks/useEcwOrders';
import Loading from '../../components/LoadingPage/Loading';
import RecordInfoTable from './InfoComponents/RecordInfoTable';
import EcwBasicInfoForm from './FormComponents/EcwBasicInfoForm';
import EcwUsageDetailsForm from './FormComponents/EcwUsageDetailsForm';
import { EcwFormInitialValues, EcwFormValues, EcwOrderResponse, getRevisedSiteRouteValue, RecordApiMessages } from './EcwConstants';
import _ from 'lodash';
import { ecwSelectedBarcodeData, ecwSelectedOrderInfo } from '../../redux/ecwOrder/ecwOrderSlice';
import { useAppSelector } from '../../redux/hooks';
import { PermissionIdentifier, PermissionModule, useModulePermissions } from '../../utils/permissionUtils';

const validationSchema = Yup.object({
    patID: Yup.string().required("Patient is required"),
    orderId: Yup.string().required("Order is required"),
    barcode: Yup.string().required("Barcode is required")
        .max(75, "Barcode should not be more than 75 characters"),
    qty: Yup.number()
        .moreThan(0, "Quantity cannot be less than or equal to 0")
        .required("Quantity is required"),
    waste: Yup.number()
        .min(0, "Waste cannot be less than 0")
        .required("Waste is required"),
    route: Yup.string().required("Route is required"),
    site: Yup.string().required("Site is required"),
});

const EcwOrderAdministration: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [recordApiMsgs, setRecordApiMsgs] = useState<RecordApiMessages | null>(null);
    const [ecwOrderResponse, setEcwOrderResponse] = useState<EcwOrderResponse | null>(null);
    const [isSelectedOrderProcessed, setIsSelectedOrderProcessed] = useState<boolean>(false);
    const [recordInfoTableData, setRecordInfoTableData] = useState<any[]>([]);
    const currentClientId = localStorage.getItem("profileClientId");
    const logInUserName = localStorage.getItem("username") ?? "";
    const logInUserfName = localStorage.getItem("firstName") ?? "";
    const logInUserlName = localStorage.getItem("lastName") ?? "";
    const selectedOrderInfo = useAppSelector(ecwSelectedOrderInfo);
    const selectedBarcodeData = useAppSelector(ecwSelectedBarcodeData);
    const { getTenantKeyAPI, getTenantKeyLoading, } = useAdmin();
    const { recordEcwOrderAPI, recordEcwOrderLoading } = useEcwOrders();

    const initialValues = useMemo(() => ({
        ...EcwFormInitialValues,
        userID: logInUserName,
        userFName: logInUserfName,
        userLName: logInUserlName,
        clientId: currentClientId ?? "",
    }), [currentClientId, logInUserName, logInUserfName, logInUserlName]);

    useEffect(() => {
        (async () => {
            try {
                const tenantRes = await getTenantKeyAPI({});
                const settingValue = tenantRes?.[0]?.settingValue;
                if (settingValue) {
                    formik.setFieldValue("accessKey", settingValue);
                }
            } catch (error) {
                console.error("Failed to fetch tenant key:", error);
            }
        })();
    }, []);

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            setRecordApiMsgs(null);
            setEcwOrderResponse(null);
            const { itemInfo, orderId, orderType, site, route, waste, facilityGuid, facilityName, ...rest } = values;
            try {
                const res = await recordEcwOrderAPI({
                    orderId,
                    orderType,
                    site: getRevisedSiteRouteValue('site', orderType, site),
                    route: getRevisedSiteRouteValue('route', orderType, route),
                    waste,
                    facilityGuid,
                    facilityName,
                    itemInfo,
                    recordUsageInfo: { ...rest, query: "false" },
                });

                // Early return if API call failed
                if (!res?.success) return;

                // Show success message if available
                if (res?.message) {
                    toast.success(res.message);
                    setRecordApiMsgs({ success: res.message, error: [] });
                }

                if (res.result?.ecwResponse) {
                    setEcwOrderResponse({
                        isOrderProcessed: res.result.ecwResponse?.isSuccess || false,
                        ecwResponseMessage: res.result.ecwResponse?.returnMessage || null,
                    });
                }

                // Update table data if result exists
                const shotData = res.result?.recordUsageInfo;
                if (shotData) {
                    setRecordInfoTableData(prev => [
                        ...prev,
                        {
                            ...shotData,
                            qty: values.qty,
                            unit: values.units,
                            waste: values.waste,
                        },
                    ]);
                }

                handleCancelForm();
            } catch (error: any) {
                const finalErrorList = error?.message
                    ? error.message.split(";").map((err: string) => _.trim(err))
                    : ["An unexpected error occurred while recording eCW data."];

                setRecordApiMsgs({ success: "", error: finalErrorList });
                console.error("Error while recording eCW data", error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleCancelForm = useCallback((isReset: boolean = false) => {
        const updatedValues: Partial<EcwFormValues> = {
            barcode: initialValues.barcode,
            qty: initialValues.qty,
            units: initialValues.units,
            waste: initialValues.waste,
            site: initialValues.site,
            route: initialValues.route,
        };

        Object.entries(updatedValues).forEach(([key, val]) => {
            formik.setFieldValue(key as any, val);
        });

        if (isReset) {
            setRecordInfoTableData([]);
            setRecordApiMsgs(null);
            setEcwOrderResponse(null);
        }

        const clearedTouched = Object.keys(updatedValues).reduce((acc: any, k) => ({ ...acc, [k]: false }), {});
        formik.setTouched({ ...formik.touched, ...clearedTouched }, false);
        formik.setErrors({});
    }, [initialValues]);

    useEffect(() => {
        if (!selectedOrderInfo || !selectedBarcodeData) {
            formik.setSubmitting(false);
            return;
        };

        if (selectedOrderInfo.medicationRequest?.rxOrderNo) {
            return;
        }

        const hasMvxData = Boolean(selectedBarcodeData?.mvxCode && selectedBarcodeData?.mvxManufacturer);
        if (hasMvxData) {
            setRecordApiMsgs(prev => ({
                error: prev?.error?.filter(err => !err.includes("MVX")) || [],
                success: prev?.success ?? "",
            }));
        }

        const itemCvx = selectedBarcodeData?.cvx;
        const orderCvx = selectedOrderInfo?.cvxCode;
        const isCvxMismatch = itemCvx !== orderCvx;
        const errorMessage = `Item CVX Code (${itemCvx?.trim()}) does not match with order CVX Code (${orderCvx?.trim()}).`;

        formik.setSubmitting(isCvxMismatch);

        setRecordApiMsgs(prev => {
            const prevErrors = prev?.error || [];
            const hasError = prevErrors.includes(errorMessage);

            return {
                error: isCvxMismatch
                    ? (hasError ? prevErrors : [...prevErrors, errorMessage])
                    : prevErrors.filter(err => !err.includes("Item CVX Code")),
                success: prev?.success ?? "",
            };
        });
    }, [selectedOrderInfo, selectedBarcodeData]);

    useEffect(() => {
        if (!formik.values.orderId) {
            setIsSelectedOrderProcessed(false);
            return;
        }
        const isProcessed = !!selectedOrderInfo && selectedOrderInfo.status?.toLowerCase() === 'administered' && selectedOrderInfo.colour?.toLowerCase() === 'green';
        setIsSelectedOrderProcessed(isProcessed);
    }, [selectedOrderInfo, formik.values.orderId]);

    useEffect(() => {
        if (recordApiMsgs?.error.length || recordApiMsgs?.success.length) {
            setRecordApiMsgs(null);
        }
    }, [formik.values.barcode, formik.values.orderId, formik.values.units]);

    const isPermitted = useModulePermissions(PermissionModule.ECW_ORDER_ADMINISTRATION);
    const isRecordPermitted = isPermitted(PermissionIdentifier.RECORD_THE_USAGE_ON_ECW);

    return (
        <section className='ecw-order-page-main'>
            <div className="page-title">
                <h2>eCW Order Administration</h2>
            </div>

            {(isLoading || getTenantKeyLoading || recordEcwOrderLoading) && <Loading />}

            <Form onSubmit={formik.handleSubmit}>
                <EcwBasicInfoForm
                    formik={formik as FormikProps<EcwFormValues>}
                    setLoading={setIsLoading}
                    recordApiMsgs={recordApiMsgs}
                    ecwOrderRes={ecwOrderResponse}
                    handleResetForm={handleCancelForm}
                />
                <EcwUsageDetailsForm
                    formik={formik as FormikProps<EcwFormValues>}
                    setLoading={setIsLoading}
                    isSelectedOrderProcessed={isSelectedOrderProcessed}
                />

                <RecordInfoTable recordTableData={recordInfoTableData} />

                <div className="form-actions">
                    <Button
                        type="button"
                        variant="outline-primary"
                        onClick={() => handleCancelForm()}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={formik.isSubmitting || isSelectedOrderProcessed || !isRecordPermitted}>Record</Button>
                </div>
            </Form>
        </section>
    )
}

export default EcwOrderAdministration;
